/**
 * Deep-merge plain JSON-like objects without mutating any input.
 *
 * Useful for combining nested translation resource bundles, where shallow
 * spread would silently wipe shared subtrees (e.g. a per-app `integrations`
 * object replacing the shared `integrations.form.*` keys).
 *
 * Rules:
 * - Recursively merges plain objects key by key.
 * - Later sources win on leaf-level conflicts.
 * - Arrays are treated as opaque values — the later array wins, no concat.
 * - Non-plain objects (Date, Map, class instances, null) are treated as leaves.
 * - No source is mutated; a fresh object is returned at every level.
 */
export function deepMerge<T extends Record<string, unknown>>(
  base: T,
  ...sources: Array<Record<string, unknown> | undefined | null>
): T {
  const out: Record<string, unknown> = {};

  for (const source of [base, ...sources]) {
    if (!isPlainObject(source)) continue;
    mergeInto(out, source as Record<string, unknown>);
  }

  return out as T;
}

function mergeInto(target: Record<string, unknown>, source: Record<string, unknown>): void {
  for (const key of Object.keys(source)) {
    const incoming = source[key];
    const existing = target[key];

    if (isPlainObject(existing) && isPlainObject(incoming)) {
      const next: Record<string, unknown> = { ...(existing as Record<string, unknown>) };
      mergeInto(next, incoming as Record<string, unknown>);
      target[key] = next;
    } else {
      // Arrays, primitives, null, and non-plain objects: later value wins as-is.
      target[key] = incoming;
    }
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  if (Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
